import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type { PrepaidAttachment, PrepaidAttachmentDownloadUrl } from '@dcc-erp/shared';

import { api, API_BASE, HttpError, type ApiError } from '../api';
import { ENTRA_API_SCOPE, msalInstance } from '../auth/msal-config';

// ============================================================
// Prepaid evidence-attachment query hooks (P7 §9.5). Uploads use
// multipart/form-data which the plain `api` JSON helper can't serialise, so we
// go through `apiMultipart` (mirrors auth + error handling of `api()` but lets
// `fetch` set the Content-Type/boundary). Same shape as asset-attachments.
// ============================================================

async function authHeaderForMultipart(): Promise<Record<string, string>> {
  const account = msalInstance.getActiveAccount() ?? msalInstance.getAllAccounts()[0];
  if (!account) return {};
  try {
    const result = await msalInstance.acquireTokenSilent({
      account,
      scopes: [ENTRA_API_SCOPE],
    });
    return { Authorization: `Bearer ${result.accessToken}` };
  } catch {
    return {};
  }
}

async function apiMultipart<T>(path: string, body: FormData): Promise<T> {
  const headers: Record<string, string> = {
    Accept: 'application/json',
    ...(await authHeaderForMultipart()),
    // Important: do NOT set Content-Type — browser must add the boundary param.
  };
  const res = await fetch(`${API_BASE}${path}`, { method: 'POST', body, headers });
  if (res.status === 401) {
    if (typeof window !== 'undefined' && window.location.pathname !== '/login') {
      window.location.href = '/login';
    }
  }
  if (!res.ok) {
    let errBody: ApiError = { status: res.status };
    try {
      const parsed = (await res.json()) as Partial<ApiError>;
      errBody = { ...errBody, ...parsed };
    } catch {
      // non-JSON error body
    }
    throw new HttpError(res.status, errBody);
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

const listKey = (companyId: string | undefined, contractId: string | undefined) =>
  ['prepaid-attachments', companyId, contractId] as const;

// Invalidate ONLY the contract-list query (key = ['prepaid-contracts', companyId,
// filtersObject]) so the register badge refreshes — without sweeping the
// per-contract detail/schedule/history sub-keys (['prepaid-contracts', companyId,
// id, …]) whose 3rd segment is the id STRING. A bare prefix invalidate would
// refetch all of those too.
function invalidateContractList(
  qc: ReturnType<typeof useQueryClient>,
  companyId: string | undefined,
) {
  void qc.invalidateQueries({
    predicate: (q) =>
      Array.isArray(q.queryKey) &&
      q.queryKey[0] === 'prepaid-contracts' &&
      q.queryKey[1] === companyId &&
      (q.queryKey.length < 3 || typeof q.queryKey[2] !== 'string'),
  });
}

export function usePrepaidAttachments(
  companyId: string | undefined,
  contractId: string | undefined,
) {
  return useQuery({
    queryKey: listKey(companyId, contractId),
    queryFn: () =>
      api<PrepaidAttachment[]>(
        `/companies/${companyId}/prepaid-contracts/${contractId}/attachments`,
      ),
    enabled: !!companyId && !!contractId,
  });
}

export interface UploadPrepaidAttachmentInput {
  file: File;
  caption?: string;
  kind?: 'IMAGE' | 'DOCUMENT';
}

export function useUploadPrepaidAttachment(
  companyId: string | undefined,
  contractId: string | undefined,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ file, caption, kind }: UploadPrepaidAttachmentInput) => {
      const fd = new FormData();
      fd.append('file', file, file.name);
      if (caption) fd.append('caption', caption);
      if (kind) fd.append('kind', kind);
      return apiMultipart<PrepaidAttachment>(
        `/companies/${companyId}/prepaid-contracts/${contractId}/attachments`,
        fd,
      );
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: listKey(companyId, contractId) });
      // The register badge derives from the contract list (missingEvidenceCount).
      invalidateContractList(qc, companyId);
    },
  });
}

export function useDeletePrepaidAttachment(
  companyId: string | undefined,
  contractId: string | undefined,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (attachmentId: string) =>
      api<PrepaidAttachment>(
        `/companies/${companyId}/prepaid-contracts/${contractId}/attachments/${attachmentId}`,
        { method: 'DELETE' },
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: listKey(companyId, contractId) });
      invalidateContractList(qc, companyId);
    },
  });
}

// Presigned URL — short-lived (5 min server-side). staleTime 4 min so React
// Query refreshes just before expiry. Fetched on-demand (enabled) per click.
export function usePrepaidAttachmentDownloadUrl(
  companyId: string | undefined,
  contractId: string | undefined,
  attachmentId: string | undefined,
  enabled: boolean,
) {
  return useQuery({
    queryKey: ['prepaid-attachment-url', companyId, contractId, attachmentId] as const,
    queryFn: () =>
      api<PrepaidAttachmentDownloadUrl>(
        `/companies/${companyId}/prepaid-contracts/${contractId}/attachments/${attachmentId}/url`,
      ),
    enabled: enabled && !!companyId && !!contractId && !!attachmentId,
    staleTime: 4 * 60 * 1000,
  });
}
